// Reusable per-event availability editor (Option B): a "default vs custom" toggle plus a
// weekly grid. Controlled by the parent (custom + rows), so it can be embedded in the
// create/edit event forms and in the standalone availability modal.

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

/** API rules → 7 editor rows (one per weekday), defaulting Mon–Fri on 09:00–17:00. */
export function rowsFromRules(rules) {
  const byDay = new Map((rules || []).map((r) => [r.day_of_week, r]));
  return DAYS.map((_d, i) => {
    const r = byDay.get(i);
    const brk = (r?.breaks || []).filter((b) => b.break_start && b.break_end);
    return {
      day_of_week: i,
      enabled: r?.enabled ?? (i >= 1 && i <= 5),
      start_time: hhmm(r?.start_time) || '09:00',
      end_time: hhmm(r?.end_time) || '17:00',
      has_break: brk.length > 0,
      break_start: brk.length > 0 ? hhmm(brk[0].break_start) : '12:00',
      break_end: brk.length > 0 ? hhmm(brk[0].break_end) : '13:00',
    };
  });
}

/** Editor rows → API rules payload (matches availabilityRuleSchema). */
export function rulesFromRows(rows) {
  return rows.map((r) => ({
    day_of_week: r.day_of_week,
    enabled: r.enabled,
    start_time: r.start_time,
    end_time: r.end_time,
    breaks: r.has_break && r.enabled ? [{ break_start: r.break_start, break_end: r.break_end }] : [],
  }));
}

/** Default 7-day rows (Mon–Fri 09:00–17:00) for a fresh custom schedule. */
export const DEFAULT_AVAIL_ROWS = rowsFromRules([]);

function Checkbox({ checked, onChange, size = 'md' }) {
  const sz = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const iconSz = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  return (
    <div
      className={`grid ${sz} place-items-center rounded border transition-colors cursor-pointer ${
        checked ? 'bg-slate-900 border-slate-900 text-white' : 'border-slate-300 bg-white hover:border-slate-400'
      }`}
      onClick={() => onChange(!checked)}
    >
      {checked && (
        <svg className={iconSz} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
  );
}

/**
 * @param {{custom:boolean, setCustom:(v:boolean)=>void,
 *          rows:Array, setRows:(rows:Array)=>void}} props
 */
export default function EventAvailabilityFields({ custom, setCustom, rows, setRows }) {
  const update = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setCustom(false)}
          className={`rounded-xl border p-3 text-left transition-colors ${!custom ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}
        >
          <p className="text-sm font-bold">Use my default availability</p>
          <p className={`mt-0.5 text-xs ${!custom ? 'text-slate-300' : 'text-slate-500'}`}>Your global Weekly Availability applies.</p>
        </button>
        <button
          type="button"
          onClick={() => setCustom(true)}
          className={`rounded-xl border p-3 text-left transition-colors ${custom ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}
        >
          <p className="text-sm font-bold">Custom hours for this event</p>
          <p className={`mt-0.5 text-xs ${custom ? 'text-indigo-100' : 'text-slate-500'}`}>Only this event uses the hours below.</p>
        </button>
      </div>

      {!custom ? (
        <p className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          This event follows your global <span className="font-semibold">Weekly Availability</span>. Switch to “Custom hours” to give it its own schedule.
        </p>
      ) : (
        <div className="mt-4 flex flex-col gap-1.5">
          {rows.map((r, i) => (
            <div key={r.day_of_week} className={`flex flex-col gap-1.5 rounded-xl px-3 py-2 border transition-colors ${r.enabled ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-50 border-slate-100 opacity-60'}`}>
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="flex w-32 shrink-0 items-center gap-2">
                  <Checkbox checked={r.enabled} onChange={(v) => update(i, { enabled: v })} />
                  <span className={`text-sm font-bold ${r.enabled ? 'text-slate-900' : 'text-slate-500'}`}>{DAYS[r.day_of_week]}</span>
                </div>
                <div className={`flex items-center gap-2 ${r.enabled ? 'opacity-100' : 'opacity-40'}`}>
                  <input type="time" value={r.start_time} disabled={!r.enabled} onChange={(e) => update(i, { start_time: e.target.value })}
                    className="h-8 w-28 rounded-full border border-slate-400 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50 disabled:text-slate-400" />
                  <span className="text-xs font-medium text-slate-400">to</span>
                  <input type="time" value={r.end_time} disabled={!r.enabled} onChange={(e) => update(i, { end_time: e.target.value })}
                    className="h-8 w-28 rounded-full border border-slate-400 bg-white px-3 text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50 disabled:text-slate-400" />
                </div>
              </div>
              {r.enabled && (
                <div className={`ml-[42px] flex flex-wrap items-center gap-3 rounded-lg px-2.5 py-1.5 ${r.has_break ? 'bg-red-50/70 border border-red-100' : ''}`}>
                  <label className="flex cursor-pointer items-center gap-2" onClick={(e) => e.preventDefault()}>
                    <Checkbox size="sm" checked={r.has_break} onChange={(v) => update(i, { has_break: v })} />
                    <span className={`text-xs font-bold ${r.has_break ? 'text-red-700' : 'text-slate-500'}`}>Add Break</span>
                  </label>
                  {r.has_break && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-red-400">from</span>
                      <input type="time" value={r.break_start} onChange={(e) => update(i, { break_start: e.target.value })}
                        className="h-7 w-24 rounded-full border border-red-200 bg-white px-2.5 text-xs font-bold text-red-700 outline-none focus:ring-2 focus:ring-red-100" />
                      <span className="text-xs font-bold text-red-400">to</span>
                      <input type="time" value={r.break_end} onChange={(e) => update(i, { break_end: e.target.value })}
                        className="h-7 w-24 rounded-full border border-red-200 bg-white px-2.5 text-xs font-bold text-red-700 outline-none focus:ring-2 focus:ring-red-100" />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
